/**
 * Detects a role-guard refusal on the scenario READ, and surfaces its message.
 * @module features/squad-planning/utils/resolveScenarioAccessDenial
 */

/** Minimal shape of the GraphQL errors Apollo attaches to a query error. */
interface GraphQLErrorLike {
  readonly message?: unknown;
  readonly extensions?: { readonly code?: unknown };
}

/**
 * Backend role guards report `FORBIDDEN`. Matched on the extension code first
 * because it is the contract; the message is only a fallback for a transport
 * that dropped extensions.
 */
const FORBIDDEN_CODE = "FORBIDDEN";

/**
 * Fallback shape of the role-guard copy ("You must be a MANAGER or above to
 * …"). Deliberately narrow: it must not match an ordinary validation message
 * that happens to mention a role.
 */
const ROLE_GUARD_MESSAGE = /\bmust be an?\s+\w+\s+or above\b/i;

/**
 * Reads every GraphQL error off an Apollo-style error, from both the places
 * they can hide.
 *
 * `graphQLErrors` covers a 200 response carrying errors. The `networkError.
 * result.errors` path matters because Apollo Client 3 classifies any non-2xx
 * response as a `ServerError`, and the backend's real message is then only
 * reachable through `result` — the top-level message degrades to "Response not
 * successful: Received status code 4xx". Same reasoning as
 * {@link resolveScenarioErrorMessage}, which reads the identical two paths.
 * @param error - The value the query failed with
 * @returns Every GraphQL error found, in the order encountered
 */
function collectGraphQLErrors(error: unknown): readonly GraphQLErrorLike[] {
  if (typeof error !== "object" || error === null) return [];

  const direct = (error as { graphQLErrors?: unknown }).graphQLErrors;
  const fromTopLevel: readonly GraphQLErrorLike[] = Array.isArray(direct)
    ? (direct as readonly GraphQLErrorLike[])
    : [];

  const networkError = (error as { networkError?: unknown }).networkError;
  const result =
    typeof networkError === "object" && networkError !== null
      ? (networkError as { result?: unknown }).result
      : undefined;
  const nested =
    typeof result === "object" && result !== null
      ? (result as { errors?: unknown }).errors
      : undefined;
  const fromNetwork: readonly GraphQLErrorLike[] = Array.isArray(nested)
    ? (nested as readonly GraphQLErrorLike[])
    : [];

  return [...fromTopLevel, ...fromNetwork];
}

/**
 * Returns the role-guard message when the scenario read was REFUSED, else null.
 *
 * Why this exists (SE-6820): `useScenarios` used to reduce the query to
 * `data?.listScenarios ?? []`, which collapses three different situations —
 * "you are not allowed", "still loading", and "you genuinely have none" — into
 * one empty array. A MEMBER therefore saw an empty scenario strip that reads as
 * "no scenarios yet" when the truth is "you cannot access this".
 *
 * The SERVER's own wording is returned rather than copy invented here, so the
 * UI cannot drift from the actual requirement if the role guard changes.
 * @param error - The error Apollo reported for the scenario list query
 * @returns The refusal message, or null when this is not a permission failure
 */
export function resolveScenarioAccessDenial(error: unknown): string | null {
  for (const entry of collectGraphQLErrors(error)) {
    const message =
      typeof entry?.message === "string" ? entry.message.trim() : "";
    const isForbidden =
      entry?.extensions?.code === FORBIDDEN_CODE ||
      ROLE_GUARD_MESSAGE.test(message);
    if (isForbidden && message.length > 0) return message;
  }
  return null;
}
